import { Icon20Cancel } from '@vkontakte/icons';
import { Button, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { duplicatesTariffsModal } from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { useDuplicatesAnalytic } from 'reactApp/hooks/duplicates/useDuplicatesAnalytic';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { setDuplicatesPromoBannerHidden } from 'reactApp/ui/Duplicates/DuplicatesPromoBanner/DuplicatesPromoBanner.helpers';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './DuplicatesPromoBanner.css';

interface IProps {
    setHidden: (hidden: boolean) => void;
}

export const DuplicatesPromoBanner: FC<IProps> = memo(function DuplicatesPromoBanner(props) {
    const { setHidden } = props;

    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const storage = useSelector(getCurrentStorage);
    const analyticData = useDuplicatesAnalytic();

    const dispatch = useDispatch();

    const onClickBuy = useTariffBuy({
        paySource: duplicatesTariffsModal.paySource,
    });

    const handleClick = useCallback(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'banner-click',
            source: storage,
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['source']],
        });

        dispatch(setDuplicatesPromoBannerHidden());

        dispatch(
            openBuyDisableAds({
                title: duplicatesTariffsModal.buyTitle,
                tariffsNumber: duplicatesTariffsModal.tariffsNumber,
                activeTab: duplicatesTariffsModal.activeTab,
                isFrame: false,
                onBuyClick: onClickBuy,
                actionShow: duplicatesTariffsModal.actionShow,
                actionClick: duplicatesTariffsModal.actionClick,
            })
        );
    }, [storage, analyticData, dispatch, onClickBuy]);

    const handleClose = useCallback(() => {
        setHidden(true);
        dispatch(setDuplicatesPromoBannerHidden());
        sendXray(['dp-ban-close', storage]);
    }, [setHidden, dispatch, storage]);

    const handleShow = useCallback(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'banner-show',
            source: storage,
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['source']],
        });
    }, [storage, analyticData]);

    useEffect(() => {
        handleShow();
    }, [handleShow]);

    const title = useMemo(() => {
        if (storage === EStorageType.home) {
            return 'С\u00A0подпиской Mail\u00A0Space можно удалить все дубликаты';
        } else if (storage === EStorageType.albums) {
            return 'Облако поможет навести порядок с\u00A0подпиской Mail Space';
        }

        return 'Удалите все\u00A0дубликаты\u00A0— с\u00A0подпиской Mail\u00A0Space';
    }, [storage]);

    const description = useMemo(() => {
        if (storage === EStorageType.home) {
            return 'Останется одно фото\u00A0— лучшее, а\u00A0у\u00A0вас\u00A0освободится место для\u00A0новых кадров';
        } else if (storage === EStorageType.albums) {
            return 'С\u00A0ней\u00A0можно удалить все\u00A0дубликаты и\u00A0освободить место для\u00A0новых фото';
        }

        return 'Сможете оставить лучшие фото и\u00A0освободить память для новых';
    }, [storage]);

    const image = `${cdnPackagesUrl}/static/cloud/promo/duplicates/promo-banner-${isDarkTheme ? 'dark' : 'light'}.webp`;

    return (
        <div className={classNames(styles.root, { [styles.root_dark]: isDarkTheme })} data-qa-id="duplicates-promo-banner">
            <img src={image} className={styles.img} data-qa-id="image" />
            <div className={styles.rightContainer}>
                <div className={styles.textContainer}>
                    <Title level="3" data-qa-id="title">
                        {title}
                    </Title>
                    <Text data-qa-id="description">{description}</Text>
                </div>
                <Button mode="primary" size="l" className={styles.button} onClick={handleClick} data-qa-id="button">
                    Попробовать
                </Button>
                <Icon20Cancel className={styles.closeIcon} onClick={handleClose} data-qa-id="close" />
            </div>
        </div>
    );
});
